<template>
  <Form>
    <Row :gutter="32" class="margin-setting-form" style="margin: 0">
      <Col span="18" class="block margin-top-20">
      <h2>{{ $t('quote.margin_setting.test_margin_setting') }}</h2>
      <div class="text-align-right margin-bottom-20">
        <router-link :to="{ name: 'menu.quote.edit', params: { quoteId: this.quoteId } }">
          {{ $t('quote.back_to_quote_page') }}
        </router-link>
      </div>
      <div>
        <vxe-toolbar perfect custom>
          <template #buttons>
            <div class="text-align-right">
              <strong>Qty: {{ quantity }}</strong>&nbsp;|&nbsp;
              <span>{{ $t('quote.info.exchange_rate') }}: {{ exchangeRate }}</span>&nbsp;|&nbsp;
              <span>{{ $t('quote.margin_setting.currencyTitle') }}</span>&nbsp;
              <Select v-model="currency" style="width: 70px" @on-change="handleCurrencyChange">
                <Option v-for="item in currencyList" :value="item.value" :key="item.value">{{ item.label }}</Option>
              </Select>
            </div>
          </template>
        </vxe-toolbar>
        <vxe-table border class="margin-setting-form--table" ref="xTable" show-footer resizable highlight-hover-row
          :footer-method="footerMethod" footer-cell-class-name="footerCellClass" :scroll-y="{ gt: -1 }"
          :span-method="spanMethod" :data="marginBreakoutTableData" :edit-config="{ trigger: 'click', mode: 'cell' }">
          <vxe-table-column type="seq" width="40" align="center"></vxe-table-column>
          <vxe-table-column field="item_group" :title="$t('quote.margin_setting.table_col_category')" width="120"
            align="center">
          </vxe-table-column>
          <vxe-table-column field="item_name" :title="$t('quote.margin_setting.table_col_subcategory')" width="160"
            align="center">
          </vxe-table-column>
          <vxe-table-column :title="$t('quote.margin_setting.table_col_cost')" width="120" align="right">
            <template v-slot="{ row }">
              <a href="#" v-if="row.cost" @click="handleShowMarginBreakDetail(row)">{{ cost(row) }}</a>
              <span v-else>{{ cost(row) }}</span>
            </template>
          </vxe-table-column>
          <vxe-table-column field="margin" width="120" :edit-render="{ name: '$input', props: { type: 'float' } }"
            :title="$t('quote.margin_setting.table_col_margin_percent')" align="right">
          </vxe-table-column>
          <vxe-table-column :title="$t('quote.margin_setting.table_col_price')" width="120" align="right">
            <template v-slot="{ row }">
              <span>{{ intlCurrencyNumber(currency, price(row), 2, 2) }} </span>
            </template>
          </vxe-table-column>
          <vxe-table-column field="tax" :title="$t('quote.margin_setting.tax')" width="110" align="right">
            <template #default="{ row }">{{ intlCurrencyNumber(currency, row.tax, 2, 2) }}</template>
          </vxe-table-column>
          <vxe-table-column width="120" title="Subtotal" align="right">
            <template v-slot="{ row }">
              <span>{{ intlCurrencyNumber(currency, price(row) + row.tax, 2, 2) }} </span>
            </template>
          </vxe-table-column>

          <vxe-table-column width="140" field="cost" :title="$t('quote.margin_setting.table_col_unit_cost_taxed')"
            align="right">
            <template v-slot="{ row }">
              <span>{{ intlCurrencyNumber(currency, unitCost(row), 2, 2) }} </span>
            </template>
          </vxe-table-column>
          <vxe-table-column width="120" field="price" :title="$t('quote.margin_setting.table_col_unit_price_taxed')"
            align="right">
            <template v-slot="{ row }">
              <span>{{ intlCurrencyNumber(currency, unitPrice(row), 2, 2) }} </span>
            </template>
          </vxe-table-column>
          <vxe-table-column width="120" field="price" :title="$t('quote.margin_setting.table_col_margin_percent')"
            align="right">
            <template v-slot="{ row }">
              <span>{{ marginTaxIncluded(row) }} </span>
            </template>
          </vxe-table-column>

          <vxe-table-column width="150" field="cost" :title="$t('quote.margin_setting.table_col_unit_cost')"
            align="right">
            <template v-slot="{ row }">
              <span>{{ intlCurrencyNumber(currency, unitCostNonTaxed(row), 2, 2) }} </span>
            </template>
          </vxe-table-column>
          <vxe-table-column width="140" field="price" :title="$t('quote.margin_setting.table_col_unit_price')"
            align="right">
            <template v-slot="{ row }">
              <span>{{ intlCurrencyNumber(currency, unitPriceNonTaxed(row), 2, 2) }} </span>
            </template>
          </vxe-table-column>
          <vxe-table-column width="120" field="marign" :title="$t('quote.margin_setting.table_col_margin_percent')"
            align="right">
            <template v-slot="{ row }">
              <span>{{ marginAccounting(row) }} </span>
            </template>
          </vxe-table-column>
          <vxe-table-column width="120" field="marign" :title="$t('quote.margin_setting.table_col_sale_tax')"
            align="right">
            <template v-slot="{ row }">
              <span>{{ intlCurrencyNumber(currency, saleTax(row), 2, 2) }} </span>
            </template>
          </vxe-table-column>

          <vxe-table-column field="remark" min-width="200"
            :edit-render="{ name: 'input', immediate: true, attrs: { type: 'text' } }"
            :title="$t('quote.margin_setting.table_col_remark')">
          </vxe-table-column>
        </vxe-table>
        <br>
        <div class="text-align-right">
          <Button class="margin-right-10" type="text" @click="handleLookHistory">{{ $t('quote.margin_setting.history')
          }}</Button>
          <Button type="primary" @click="saveMarginBreakdownsData">{{ $t('save') }}</Button>
        </div>
      </div>
      <Modal v-model="showMarginBreakDetail" title="" width="70%" footer-hide :transfer="false" :z-index="1001">
        <MarginSettingDetailForm :detailData="detailData" :currency="currency" :loading="loading">
        </MarginSettingDetailForm>
      </Modal>
      <Modal v-model="showMarginBreakHistory" title="" width="60%" footer-hide>
        <div class="margin-top-20">
          <vxe-table border max-height="450px" ref="historyTable" :loading="loading" highlight-current-row
            highlight-hover-row resizable :data="historyList">
            <vxe-table-column type="seq" width="40"></vxe-table-column>
            <vxe-table-column field="created_at" :title="$t('quote.margin_setting.table_time')" min-width="180"
              align="right">
            </vxe-table-column>
            <vxe-table-column field="user.name" :title="$t('quote.margin_setting.table_user')" min-width="140"
              align="right">
            </vxe-table-column>
            <vxe-table-column :title="$t('quote.margin_setting.table_total')" min-width="140" align="right">
              <template v-slot="{ row }">
                <span>{{ intlCurrencyNumber(currency, row.margin_summary.amount, 2, 2) }} </span>
              </template>
            </vxe-table-column>
            <vxe-table-column field="margin_summary.overall_margin"
              :title="$t('quote.margin_setting.table_col_margin_percent')" min-width="140" align="right">
            </vxe-table-column>
            <vxe-table-column :title="$t('quote.margin_setting.table_action')" min-width="100">
              <template v-slot="{ row }">
                <Button type="text" @click="handleSelectHistory(row)" icon="md-eye"></Button>
              </template>
            </vxe-table-column>
          </vxe-table>
        </div>
      </Modal>
      </Col>
      <Col span="6" style="padding-right: 0">
      <div class="block margin-top-20 preview-summary">
        <h3>Preview</h3>
        <!-- <FormItem :label="$t('quote.margin_setting.table_col_unit_price_taxed')">
            <span style="font-size: 120%; font-weight: bold;">
              {{ intlCurrencyNumber(this.currency, summary.amount / quantity, 2, 2) }}
            </span>
          </FormItem> -->
        <FormItem :label="$t('quote.margin_setting.table_col_unit_price')">
          <span style="font-size: 120%; font-weight: bold;">
            {{ intlCurrencyNumber(this.currency, summary.amount / (1 + this.defaultSetting.tax_rate) / quantity, 2, 2)
            }}
          </span>
        </FormItem>
        <FormItem :label="$t('quantity')">
          <span style="font-size: 120%; font-weight: bold;">{{ quantity }}</span>
        </FormItem>
        <FormItem :label="$t('quote.info.total_tax')">
          <span style="font-size: 120%; font-weight: bold;">{{ intlCurrencyNumber(this.currency, summary.amount / (1 +
              this.defaultSetting.tax_rate) * this.defaultSetting.tax_rate, 2, 2)
          }}</span>
        </FormItem>
        <FormItem :label="$t('quote.margin_setting.total')">
          <span style="font-size: 120%; font-weight: bold;">{{ intlCurrencyNumber(this.currency, summary.amount, 2, 2)
          }}</span>
        </FormItem>
        <FormItem :label="$t('quote.margin_setting.overall_margin')">
          <span style="font-size: 120%; font-weight: bold;">
            {{ marginValue }}
          </span>
        </FormItem>

        <Divider dashed></Divider>
        <FormItem :label="$t('quote.margin_setting.total_cost')">
          <span style="font-size: 120%; font-weight: bold;">
            {{ intlCurrencyNumber(this.currency, summary.total_cost + summary.other_expenses, 2, 2) }}
          </span>
        </FormItem>
        <FormItem :label="$t('quote.margin_setting.overall_margin')">
          <span v-if="!overallMarginChangable" style="font-size: 120%; font-weight: bold;">
            {{ intlCurrencyNumber(this.currency, summary.profit, 2, 2) }} ({{
                overallMarginFormat(setting.overall_margin)
            }})
          </span>
          <InputNumber v-else v-model="setting.overall_margin" style="width: auto;" :formatter="overallMarginFormat">
          </InputNumber>
        </FormItem>

        <Divider dashed></Divider>
        <FormItem :label="$t('quote.margin_setting.total_cost_all')">
          <span style="font-size: 120%; font-weight: bold;">
            {{ intlCurrencyNumber(this.currency, summary.total_cost + summary.total_tax + summary.other_expenses, 2, 2)
            }}
          </span>
        </FormItem>
        <FormItem :label="$t('quote.margin_setting.overall_margin_2')">
          <span style="font-size: 120%; font-weight: bold;">
            {{ overallMarginFormat((summary.amount - summary.total_cost - summary.total_tax - summary.other_expenses) /
                summary.amount)
            }}
          </span>
        </FormItem>
      </div>

      <div class="block margin-top-20">
        <h3>{{ $t('quote.margin_setting.additional_expenses') }}</h3>
        <FormItem :label="$t('quote.margin_setting.expenses.qc_cost')">
          <InputNumber :formatter="value => `￥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
            :parser="value => value.replace(/\￥\s?|(,*)/g, '')" v-model="additionalCostData.qc_cost"></InputNumber>
        </FormItem>
        <FormItem :label="$t('quote.margin_setting.expenses.company_expenses')">
          <InputNumber :formatter="value => `￥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
            :parser="value => value.replace(/\￥\s?|(,*)/g, '')" v-model="additionalCostData.company_expenses">
          </InputNumber>
        </FormItem>
        <div class="text-align-right">
          <Button type="primary" @click="handleSaveCosts">{{ $t('save') }}</Button>
        </div>
      </div>
      </Col>
    </Row>
  </Form>
</template>

<script>
import XEUtils from 'xe-utils'
import { mapActions } from 'vuex'
import { intlCurrencyNumber, currencyConvert } from '@/libs/util'
import MarginSettingDetailForm from './margin-setting-detail-form'
import CONFIG from '@/config'

export default {
  name: 'MarginSettingForm',
  components: {
    MarginSettingDetailForm
  },
  props: {
    defaultSetting: {
      type: Object,
      required: true
    },
    quoteId: {
      type: [Number, String],
      required: true
    },
    quantity: {
      type: [Number, String],
      required: true
    },
    exchangeRate: {
      type: [Number, String],
      default: CONFIG.defaultChangeRate
    },
    currencyDefault: {
      type: String,
      default: 'CNY'
    },
    additionalCostParams: {
      type: Object,
      required: true
    },
    costEstimatorId: {
      type: Number,
      required: true
    }
  },
  mounted() {
    console.log(this.exchangeRate)

    this.getMarginBreakdownsData(this.quoteId)
    this.getMarginSummaryData(this.quoteId)
  },
  computed: {
    amtWithoutSaleTax() {
      return this.summary.amount / (1 + this.defaultSetting.tax_rate)
    },
    allCostAmt() {
      return this.summary.total_cost + this.summary.other_expenses
    },
    marginValue() {
      return XEUtils.round(XEUtils.divide(this.amtWithoutSaleTax - this.allCostAmt, this.amtWithoutSaleTax) * 100, 2) + '%'
    }
  },
  data() {
    return {
      setting: {
        overall_margin: 0.0000,
        remark: ''
      },
      overallMarginChangable: false,
      summary: {},
      marginBreakoutTableData: [],
      grossProfitSum: 0,
      priceSum: 0,
      taxSum: 0,
      currency: this.currencyDefault,
      showMarginBreakDetail: false,
      detailData: [],
      loading: true,
      currencyList: [
        { label: 'CNY', value: 'CNY' },
        { label: 'USD', value: 'USD' }
      ],
      showMarginBreakHistory: false,
      historyList: [],
      additionalCostData: this.additionalCostParams
    }
  },
  methods: {
    ...mapActions([
      'getMarginBreakdowns',
      'getMarginSummary',
      'saveCosts',
      'saveMarginBreakdowns',
      'getMarginBreakDetail',
      'getMarginSnapshots'
    ]),
    intlCurrencyNumber,
    currencyConvert,
    getMarginBreakdownsData(quoteId) {
      this.getMarginBreakdowns(quoteId).then(resp => {
        resp.data.data.forEach((item) => {
          item.margin = XEUtils.multiply(item.margin, 100)
        })
        this.marginBreakoutTableData = resp.data.data
        if (this.marginBreakoutTableData.length > 0 && (resp.data.currency !== this.currency)) {
          this.marginBreakoutTableData.forEach((item) => {
            item.cost = this.currencyConvert(this.currency, item.cost, this.exchangeRate)
            item.tax = this.currencyConvert(this.currency, item.tax, this.exchangeRate)
          })
          this.$refs.xTable.loadData(this.marginBreakoutTableData)
        }
      })
    },
    getMarginSummaryData(quoteId) {
      this.getMarginSummary(quoteId).then(resp => {
        this.summary = resp.data
        this.setting.overall_margin = resp.data.overall_margin || 0
        if (this.summary.currency !== this.currency) {
          this.summary.total_cost = this.currencyConvert(this.currency, this.summary.total_cost, this.exchangeRate)
          this.summary.other_expenses = this.currencyConvert(this.currency, this.summary.other_expenses, this.exchangeRate)
          this.summary.profit = this.currencyConvert(this.currency, this.summary.profit, this.exchangeRate)
          this.summary.total_tax = this.currencyConvert(this.currency, this.summary.total_tax, this.exchangeRate)
          this.summary.amount = this.currencyConvert(this.currency, this.summary.amount, this.exchangeRate)
        }
      })
    },
    spanMethod({ row, rowIndex, $rowIndex, column, columnIndex, $columnIndex, data }) {
      const fields = ['item_group']
      const cellValue = XEUtils.get(row, column.property)

      if (cellValue && fields.includes(column.property)) {
        const prevRow = data[rowIndex - 1]
        let nextRow = data[rowIndex]

        if (prevRow && XEUtils.get(prevRow, column.property) === cellValue) {
          return { rowspan: 0, colspan: 0 }
        } else {
          let countRowspan = 0

          while (nextRow && XEUtils.get(nextRow, column.property) === cellValue) {
            nextRow = data[++countRowspan + rowIndex]
          }
          if (countRowspan > 1) {
            return { rowspan: countRowspan, colspan: 1 }
          }
        }
      }
    },
    footerMethod({ columns, columnIndex, data }) {
      const footerData = [
        columns.map((column, columnIndex) => {
          if (columnIndex === 2) {
            return 'Overall'
          }

          // Cost
          if (columnIndex === 3) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => row.cost)), 2, 2)
          }
          // Margin %
          if (columnIndex === 4) {
            var totalAmt = XEUtils.sum(data.map(row => XEUtils.multiply(row.cost, row.margin / 100 + 1)))
            var totalCost = XEUtils.sum(data.map(row => row.cost))
            return XEUtils.round(XEUtils.divide(totalAmt - totalCost, totalAmt) * 100, 2) + '%'
          }
          // Price
          if (columnIndex === 5) {
            this.priceSum = XEUtils.sum(data.map(row => this.price(row)))
            return this.intlCurrencyNumber(this.currency, this.priceSum, 2, 2)
          }
          // Tax
          if (columnIndex === 6) {
            this.taxSum = XEUtils.sum(data.map(row => row.tax))
            return this.intlCurrencyNumber(this.currency, this.taxSum, 2, 2)
          }
          // Subtotal
          if (columnIndex === 7) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => this.price(row) + row.tax)), 2, 2)
          }

          // Unit cost
          if (columnIndex === 8) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => this.unitCost(row))), 2, 2)
          }

          // Unit price
          if (columnIndex === 9) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => this.unitPrice(row))), 2, 2)
          }
          // Margin tax included
          if (columnIndex === 10) {
            var totalAmtWithTax = XEUtils.sum(data.map(row => this.unitPrice(row)))
            var totalCostWithTax = XEUtils.sum(data.map(row => this.unitCost(row)))
            return XEUtils.round(XEUtils.divide(totalAmtWithTax - totalCostWithTax, totalAmtWithTax) * 100, 2) + '%'
          }

          // Unit cost non-taxed
          if (columnIndex === 11) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => this.unitCostNonTaxed(row))), 2, 2)
          }

          // Unit price non-taxed
          if (columnIndex === 12) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => this.unitPriceNonTaxed(row))), 2, 2)
          }

          // Margin accounting
          if (columnIndex === 13) {
            var totalAmtNonTax = XEUtils.sum(data.map(row => this.unitPriceNonTaxed(row)))
            var totalCostNonTax = XEUtils.sum(data.map(row => this.unitCostNonTaxed(row)))
            return XEUtils.round(XEUtils.divide(totalAmtNonTax - totalCostNonTax, totalAmtNonTax) * 100, 2) + '%'
          }

          // Sale tax amount
          if (columnIndex === 14) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => this.saleTax(row))), 2, 2)
          }

          return null
        })
      ]
      return footerData
    },
    grossProfit(row) {
      return XEUtils.round(XEUtils.multiply(row.cost, row.margin / 100), 5)
    },
    marginTaxIncluded(row) {
      return XEUtils.round(XEUtils.multiply(XEUtils.divide((this.unitPrice(row) - this.unitCost(row)), this.unitPrice(row)), 100), 2) + '%'
    },
    marginAccounting(row) {
      return XEUtils.round(XEUtils.multiply(XEUtils.divide((this.unitPriceNonTaxed(row) - this.unitCostNonTaxed(row)), this.unitPriceNonTaxed(row)), 100), 2) + '%'
    },
    price(row) {
      return XEUtils.round(XEUtils.multiply(row.cost, row.margin / 100 + 1), 5)
    },
    cost(row) {
      return this.intlCurrencyNumber(this.currency, row.cost, 2, 2)
    },
    unitCost(row) {
      return XEUtils.round((row.cost + row.tax) / this.quantity, 5)
    },
    unitCostNonTaxed(row) {
      return XEUtils.round(row.cost / this.quantity, 5)
    },
    unitPrice(row) {
      return XEUtils.round((XEUtils.multiply(row.cost, row.margin / 100 + 1) + row.tax) / this.quantity, 5)
    },
    unitPriceNonTaxed(row) {
      return XEUtils.round(XEUtils.divide(this.unitPrice(row), (1 + this.defaultSetting.tax_rate)), 5)
    },
    saleTax(row) {
      return XEUtils.round(this.unitPrice(row) - this.unitPriceNonTaxed(row), 5)
    },
    saveMarginBreakdownsData() {
      let formData = XEUtils.clone(this.marginBreakoutTableData, true)
      formData.forEach((item) => {
        item.margin = XEUtils.divide(item.margin, 100)
      })
      let formDataObj = {}
      formDataObj.data = formData
      this.saveMarginBreakdowns({ quoteId: this.quoteId, data: formDataObj }).then(resp => {
        this.$Message.success('Margin breakdowns has been saved successfully!')
        this.getMarginSummaryData(this.quoteId)
      })
    },
    overallMarginFormat(value) {
      return XEUtils.round(XEUtils.multiply(value, 100), 2) + '%'
    },
    handleShowMarginBreakDetail(row) {
      this.detailData = []
      this.loading = true
      if (row && row.report_name) {
        this.getMarginBreakDetail({ quoteId: this.quoteId, reportName: row.report_name }).then(resp => {
          this.detailData = resp.data.data
          this.loading = false
          if ((resp.data.currency !== this.currency) && this.detailData.length > 0) {
            this.detailData.forEach((item) => {
              item.price = this.currencyConvert(this.currency, item.price, this.exchangeRate)
              item.price_amount = this.currencyConvert(this.currency, item.price_amount, this.exchangeRate)
              item.vat_amount = this.currencyConvert(this.currency, item.vat_amount, this.exchangeRate)
            })
          }
        })
      }
      this.showMarginBreakDetail = true
    },
    handleCurrencyChange(currency) {
      if (this.marginBreakoutTableData.length > 0) {
        this.marginBreakoutTableData.forEach((item) => {
          item.cost = this.currencyConvert(currency, item.cost, this.exchangeRate)
          item.tax = this.currencyConvert(currency, item.tax, this.exchangeRate)
        })
        this.$refs.xTable.loadData(this.marginBreakoutTableData)
      }
      if (this.summary) {
        this.summary.total_cost = this.currencyConvert(currency, this.summary.total_cost, this.exchangeRate)
        this.summary.other_expenses = this.currencyConvert(currency, this.summary.other_expenses, this.exchangeRate)
        this.summary.profit = this.currencyConvert(currency, this.summary.profit, this.exchangeRate)
        this.summary.total_tax = this.currencyConvert(currency, this.summary.total_tax, this.exchangeRate)
        this.summary.amount = this.currencyConvert(currency, this.summary.amount, this.exchangeRate)
      }
    },
    handleLookHistory() {
      this.showMarginBreakHistory = true
      this.historyList = []
      this.loading = true
      this.getMarginSnapshots(this.quoteId).then(res => {
        this.loading = false
        if (res.data) {
          this.historyList = res.data
          this.historyList.forEach((item) => {
            item.margin_summary.overall_margin = XEUtils.round(item.margin_summary.overall_margin * 100, 2)
            if (item.margin_summary.currency !== this.currency) {
              item.margin_summary.amount = this.currencyConvert(this.currency, item.margin_summary.amount, this.exchangeRate)
            }
          })
        }
      })
    },
    handleSelectHistory(row) {
      let routeData = this.$router.resolve({ name: 'menu.quote.margin_setting.history', params: { quoteId: this.quoteId, historyId: row.id } })
      window.open(routeData.href, '_blank')
    },
    handleSaveCosts() {
      let costObj = {}
      costObj.cost_estimator_id = this.costEstimatorId
      costObj.qc_cost = this.additionalCostData.qc_cost
      costObj.company_expenses = this.additionalCostData.company_expenses
      this.saveCosts({ quoteId: this.quoteId, data: costObj }).then(resp => {
        this.$Message.success('Costs has been updated successfully!')
        this.getMarginBreakdownsData(this.quoteId)
        this.getMarginSummaryData(this.quoteId)
      })
    }
  },
  watch: {
    defaultSetting: {
      deep: true,
      handler() {
        this.setting = this.defaultSetting
      }
    }
  }
}
</script>
<style lang="less">
.footerCellClass {
  background-color: #F8F8F8;
  font-weight: bold;
}

.margin-setting-form--table {
  table.vxe-table--header {
    th:nth-child(5) {
      color: #2D8cF0;
    }

    th:nth-child(3),
    th:nth-child(8),
    th:nth-child(11),
    th:nth-child(15) {
      border-right: 2px solid #b5b5b6;
    }
  }

  table.vxe-table--footer {

    td:nth-child(3),
    td:nth-child(8),
    td:nth-child(11),
    td:nth-child(15) {
      border-right: 2px solid #b5b5b6;
    }
  }

  table.vxe-table--body {
    .col--edit {
      text-decoration: underline;
      color: #2D8cF0;
    }

    tr:nth-child(1),
    tr:nth-child(3),
    tr:nth-child(8) {

      td:nth-child(3),
      td:nth-child(8),
      td:nth-child(11),
      td:nth-child(15) {
        border-right: 2px solid #b5b5b6;
      }
    }

    tr:nth-child(2),
    tr:nth-child(4),
    tr:nth-child(5),
    tr:nth-child(6),
    tr:nth-child(7),
    tr:nth-child(9),
    tr:nth-child(10),
    tr:nth-child(11),
    tr:nth-child(12) {

      td:nth-child(2),
      td:nth-child(7),
      td:nth-child(10),
      td:nth-child(14) {
        border-right: 2px solid #b5b5b6;
      }
    }
  }
}

.preview-summary .ivu-form-item {
  display: flex;

  .ivu-form-item-label {
    text-align: left;
    width: 170px;
  }
}
</style>
